import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnsType } from 'antd/es/table';

import { IProduct, IProductVariation } from '@/models/products.model';
import { ProductsAPI } from '@/api';
import { useAppDispatch } from '@/store';
import { addProductVariationsBulk, getProductVariations } from '../../feature';
import { Checkbox, Table } from '@/ui';
import { Modal, ModalSize, Pagination, showErrorToast, showSuccessToast } from '@/components';

import SkuArticlesInput from './SkuArticlesInput';
import { productMatchesAnyArticle } from './helpers';
import {
  StyledAddButton,
  StyledAddedInfo,
  StyledAddedItem,
  StyledAddedName,
  StyledAddedSku,
  StyledCancelButton,
  StyledColumn,
  StyledColumnBody,
  StyledColumnHeader,
  StyledColumns,
  StyledColumnTitle,
  StyledCountBadge,
  StyledErrors,
  StyledFooter,
  StyledModalBody,
  StyledPaginationWrap,
  StyledProductCell,
  StyledProductImage,
  StyledProductName,
  StyledSaveButton,
  StyledSelectAll,
  StyledSubtitle,
  StyledTitle,
} from './BulkAddVariationsContainerStyles';

type TBulkAddVariationsContainerProps = {
  visible: boolean;
  productId: string;
  existingVariations: IProductVariation[];
  onClose: () => void;
};

const PAGE_SIZE_OPTIONS = ['5', '10', '20'];

const BulkAddVariationsContainer = ({
  visible,
  productId,
  existingVariations,
  onClose,
}: TBulkAddVariationsContainerProps) => {
  const { t } = useTranslation(['products']);
  const dispatch = useAppDispatch();
  const [articles, setArticles] = useState<string[]>([]);
  const [notFound, setNotFound] = useState<string[]>([]);
  const [results, setResults] = useState<IProduct[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const alreadyAddedVariations = existingVariations.filter((variation) => variation.id !== productId);

  const resetState = () => {
    setArticles([]);
    setNotFound([]);
    setResults([]);
    setSelectedIds([]);
    setPage(1);
    setPageSize(10);
    setTotal(0);
    setHasSearched(false);
  };

  useEffect(() => {
    if (visible) {
      resetState();
    }
  }, [visible]);

  const searchVariations = async (nextArticles = articles, nextPage = page, nextPageSize = pageSize) => {
    if (!nextArticles.length) {
      return;
    }

    setIsSearching(true);
    try {
      const response = await ProductsAPI.bulkSearchProductVariations(productId, {
        articles: nextArticles,
        page: nextPage,
        limit: nextPageSize,
      });
      setResults(response.data?.data ?? []);
      setTotal(response.data?.meta?.total ?? 0);
      setNotFound(response.data?.not_found ?? []);
      setPage(response.data?.meta?.current_page ?? nextPage);
      setPageSize(response.data?.meta?.per_page ?? nextPageSize);
      setHasSearched(true);
      setSelectedIds((current) => {
        const resultIds = new Set((response.data?.data ?? []).map((product) => product.id));
        return current.filter((id) => resultIds.has(id));
      });
    } catch {
      showErrorToast({ message: t('product_update_validation_error') });
    } finally {
      setIsSearching(false);
    }
  };

  const handleFind = (nextArticles: string[]) => {
    setPage(1);
    setSelectedIds([]);
    searchVariations(nextArticles, 1, pageSize);
  };

  const handleArticlesChange = (nextArticles: string[]) => {
    setArticles(nextArticles);
    const normalizedArticles = new Set(nextArticles.map((article) => article.toLowerCase()));
    setNotFound((current) => current.filter((article) => normalizedArticles.has(article.toLowerCase())));

    if (!nextArticles.length) {
      setResults([]);
      setSelectedIds([]);
      setNotFound([]);
      setHasSearched(false);
      setTotal(0);
      return;
    }

    setResults((current) => {
      const filtered = current.filter((product) => productMatchesAnyArticle(product, nextArticles));
      const filteredIds = new Set(filtered.map((product) => product.id));
      setSelectedIds((ids) => ids.filter((id) => filteredIds.has(id)));
      return filtered;
    });
  };

  const toggleSelected = (id: string, checked: boolean) => {
    setSelectedIds((current) => {
      if (checked) {
        return current.includes(id) ? current : [...current, id];
      }
      return current.filter((item) => item !== id);
    });
  };

  const pageIds = results.map((product) => product.id);
  const isAllPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));

  const handleSelectAllPage = (checked: boolean) => {
    setSelectedIds((current) => {
      if (checked) {
        return Array.from(new Set([...current, ...pageIds]));
      }
      return current.filter((id) => !pageIds.includes(id));
    });
  };

  const handlePageChange = (nextPage: number, nextPageSize?: number) => {
    const size = nextPageSize ?? pageSize;
    const pageToLoad = size !== pageSize ? 1 : nextPage;
    setPage(pageToLoad);
    setPageSize(size);
    searchVariations(articles, pageToLoad, size);
  };

  const handleAdd = async () => {
    if (!selectedIds.length) {
      return;
    }

    setIsSubmitting(true);
    try {
      await dispatch(addProductVariationsBulk({ product_id: productId, variation_ids: selectedIds })).unwrap();
      await dispatch(getProductVariations(productId)).unwrap();
      showSuccessToast({ message: t('product_variations_bulk_add_success') });
      setSelectedIds([]);
      if (articles.length) {
        await searchVariations(articles, page, pageSize);
      }
    } catch {
      showErrorToast({ message: t('product_update_validation_error') });
    } finally {
      setIsSubmitting(false);
    }
  };

  const columns: ColumnsType<IProduct> = [
    {
      title: '',
      dataIndex: 'id',
      width: 40,
      fixed: 'left',
      render: (id: string) => (
        <Checkbox checked={selectedIds.includes(id)} onChange={(event) => toggleSelected(id, event.target.checked)} />
      ),
    },
    {
      title: t('product_variations_table_col_product'),
      dataIndex: 'name',
      ellipsis: true,
      render: (_name: string, record) => (
        <StyledProductCell>
          <StyledProductImage src={record.photo || ''} alt="" />
          <StyledProductName>{record.name}</StyledProductName>
        </StyledProductCell>
      ),
    },
    {
      title: t('product_variations_table_col_gost_sku'),
      dataIndex: 'sku',
      width: 96,
      ellipsis: true,
    },
    {
      title: t('product_variations_table_col_partner_sku'),
      dataIndex: 'external_id',
      width: 96,
      ellipsis: true,
    },
  ];

  return (
    <Modal visible={visible} onCancel={onClose} size={ModalSize.HUGE} destroyOnClose>
      <StyledModalBody>
        <div>
          <StyledTitle>{t('product_variations_bulk_title')}</StyledTitle>
          <StyledSubtitle>{t('product_variations_bulk_subtitle')}</StyledSubtitle>
        </div>
        <SkuArticlesInput
          articles={articles}
          onArticlesChange={handleArticlesChange}
          onFind={handleFind}
          isSearching={isSearching}
        />
        {notFound.length > 0 && (
          <StyledErrors>
            {notFound.map((article) => (
              <li key={article}>{t('product_variations_bulk_not_found', { article })}</li>
            ))}
          </StyledErrors>
        )}
        <StyledColumns>
          <StyledColumn>
            <StyledColumnHeader>
              <StyledColumnTitle>
                {t('product_variations_bulk_new_title')}
                <span>{t('product_variations_bulk_selected', { count: selectedIds.length })}</span>
                <StyledCountBadge>{selectedIds.length}</StyledCountBadge>
              </StyledColumnTitle>
            </StyledColumnHeader>
            <StyledSelectAll>
              <Checkbox
                checked={isAllPageSelected}
                disabled={!pageIds.length}
                onChange={(event) => handleSelectAllPage(event.target.checked)}
              />
              {t('product_variations_bulk_select_all')}
            </StyledSelectAll>
            <StyledColumnBody>
              <Table
                columns={columns}
                dataSource={results}
                rowKey="id"
                loading={isSearching}
                tableLayout="fixed"
                scroll={{ x: 480 }}
                {...(!hasSearched ? { locale: { emptyText: null } } : {})}
              />
            </StyledColumnBody>
            {hasSearched && (
              <StyledPaginationWrap>
                <Pagination
                  current={page}
                  pageSize={pageSize}
                  total={total}
                  showSizeChanger
                  pageSizeOptions={PAGE_SIZE_OPTIONS}
                  onChange={handlePageChange}
                />
              </StyledPaginationWrap>
            )}
          </StyledColumn>
          <StyledColumn>
            <StyledColumnHeader>
              <StyledColumnTitle>
                {t('product_variations_bulk_already_title', { count: alreadyAddedVariations.length })}
              </StyledColumnTitle>
            </StyledColumnHeader>
            <StyledColumnBody>
              {alreadyAddedVariations.map((variation) => (
                <StyledAddedItem key={variation.id}>
                  <StyledProductImage src={variation.photo || ''} alt="" />
                  <StyledAddedInfo>
                    <StyledAddedName>{variation.name}</StyledAddedName>
                    <StyledAddedSku>
                      {variation.sku}
                      {variation.external_id ? ` · ${variation.external_id}` : ''}
                    </StyledAddedSku>
                  </StyledAddedInfo>
                </StyledAddedItem>
              ))}
            </StyledColumnBody>
          </StyledColumn>
        </StyledColumns>
        <StyledFooter>
          <StyledCancelButton onClick={onClose}>{t('product_variations_bulk_cancel')}</StyledCancelButton>
          <StyledSaveButton onClick={onClose}>{t('product_variations_bulk_save')}</StyledSaveButton>
          <StyledAddButton onClick={handleAdd} disabled={!selectedIds.length} loading={isSubmitting}>
            {t('product_variations_bulk_add', { count: selectedIds.length })}
          </StyledAddButton>
        </StyledFooter>
      </StyledModalBody>
    </Modal>
  );
};

export default BulkAddVariationsContainer;
